/* xfeat.js — XFeat post-processing + C4-steered matching + RANSAC homography.
 *
 * Pure compute, no DOM. Runs in browser and Node (validated against the
 * PyTorch reference implementation used to export the ONNX model).
 *
 * The ONNX graph (export_xfeat_onnx.py) outputs:
 *   feats [1,64,h8,w8]  L2-normalized dense descriptors (NCHW flat)
 *   kheat [1,1,H,W]     keypoint heatmap
 *   rel   [1,1,h8,w8]   reliability map
 * This file implements everything XFeat.detectAndCompute does after that:
 * NMS -> scores -> top-k -> bicubic descriptor sampling -> L2 norm,
 * plus the vismatch steered-matching loop and homography estimation.
 */

function perfNow() { return globalThis.performance?.now ? performance.now() : Date.now(); }
function postTiming(msg) {
  try { if (typeof self !== "undefined" && self.postMessage) self.postMessage({ id: -1, kind: "diag", msg }); } catch {}
}

// ---------------------------------------------------------------- detection

export function detectAndCompute(feats, kheat, rel, H, W, opts = {}) {
  const topK = opts.topK ?? 1024;
  const threshold = opts.threshold ?? 0.05;
  const h8 = H / 8, w8 = W / 8;

  // --- NMS: 5x5 local maxima above threshold (torch: x == maxpool(x)) ---
  const cands = [];
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const v = kheat[y * W + x];
      if (v <= threshold) continue;
      let isMax = true;
      const y0 = Math.max(0, y - 2), y1 = Math.min(H - 1, y + 2);
      const x0 = Math.max(0, x - 2), x1 = Math.min(W - 1, x + 2);
      for (let yy = y0; yy <= y1 && isMax; yy++) {
        for (let xx = x0; xx <= x1; xx++) {
          if (kheat[yy * W + xx] > v) { isMax = false; break; }
        }
      }
      if (!isMax) continue;
      // score = heatmap value * bilinear(reliability) [grid_sample, align_corners=false]
      const s = v * bilinearAt(rel, h8, w8, x, y, H, W);
      cands.push({ x, y, s });
    }
  }

  // --- top-k (stable sort matches torch argsort tie behavior) ---
  cands.sort((a, b) => b.s - a.s);
  const n = Math.min(topK, cands.length);
  const kpts = new Float32Array(n * 2);
  const scores = new Float32Array(n);
  const descs = new Float32Array(n * 64);

  for (let i = 0; i < n; i++) {
    kpts[i * 2] = cands[i].x;
    kpts[i * 2 + 1] = cands[i].y;
    scores[i] = cands[i].s;
    bicubicDesc(feats, h8, w8, cands[i].x, cands[i].y, H, W, descs, i * 64);
    // L2 normalize
    let nrm = 0;
    for (let c = 0; c < 64; c++) nrm += descs[i * 64 + c] ** 2;
    nrm = 1 / Math.max(Math.sqrt(nrm), 1e-12);
    for (let c = 0; c < 64; c++) descs[i * 64 + c] *= nrm;
  }
  return { kpts, scores, descs, n };
}

// grid_sample(align_corners=false) coordinate transform: full-res pixel -> feature-map coords
function gsCoord(p, sizeImgMinus1, sizeFeat) {
  const norm = 2 * (p / sizeImgMinus1) - 1;
  return ((norm + 1) * sizeFeat - 1) / 2;
}

function bilinearAt(map, h, w, px, py, H, W) {
  const gx = gsCoord(px, W - 1, w), gy = gsCoord(py, H - 1, h);
  const x0 = Math.floor(gx), y0 = Math.floor(gy);
  let out = 0;
  for (let dy = 0; dy <= 1; dy++) {
    for (let dx = 0; dx <= 1; dx++) {
      const xi = x0 + dx, yi = y0 + dy;
      if (xi < 0 || xi >= w || yi < 0 || yi >= h) continue; // zero padding
      const wgt = (1 - Math.abs(gx - xi)) * (1 - Math.abs(gy - yi));
      out += map[yi * w + xi] * wgt;
    }
  }
  return out;
}

// torch bicubic (cubic convolution, A = -0.75), zero padding
function cubicW(t) {
  const A = -0.75; t = Math.abs(t);
  if (t <= 1) return ((A + 2) * t - (A + 3)) * t * t + 1;
  if (t < 2) return (((t - 5) * t + 8) * t - 4) * A;
  return 0;
}

function bicubicDesc(feats, h, w, px, py, H, W, out, o) {
  const gx = gsCoord(px, W - 1, w), gy = gsCoord(py, H - 1, h);
  const x0 = Math.floor(gx), y0 = Math.floor(gy);
  const fx = gx - x0, fy = gy - y0;
  for (let c = 0; c < 64; c++) out[o + c] = 0;
  const plane = h * w;
  for (let j = -1; j <= 2; j++) {
    const yi = y0 + j;
    if (yi < 0 || yi >= h) continue;
    const wy = cubicW(fy - j);
    for (let i = -1; i <= 2; i++) {
      const xi = x0 + i;
      if (xi < 0 || xi >= w) continue;
      const wgt = cubicW(fx - i) * wy;
      if (wgt === 0) continue;
      const base = yi * w + xi;
      for (let c = 0; c < 64; c++) out[o + c] += feats[c * plane + base] * wgt;
    }
  }
}

// ---------------------------------------------------------------- steering

// Perm steerer: descriptor channels as 4 blocks of 16; rotating the image by
// k*90deg <=> rolling the blocks by k (torch arange(64).reshape(4,16).roll(k,0))
export function steerPermIndex(r) {
  const idx = new Int32Array(64);
  for (let c = 0; c < 64; c++) {
    const block = Math.floor(c / 16), pos = c % 16;
    idx[c] = (((block - r) % 4 + 4) % 4) * 16 + pos; // roll(k): out[b] = in[b-k]
  }
  return idx;
}

export function applyPermSteer(descs, n, r) {
  const idx = steerPermIndex(r);
  const out = new Float32Array(n * 64);
  for (let i = 0; i < n; i++)
    for (let c = 0; c < 64; c++) out[i * 64 + c] = descs[i * 64 + idx[c]];
  return out;
}

// Learned steerer: one application of 64x64 W + L2 norm (vismatch semantics)
export function applyLearnedSteer(descs, n, Wm) {
  const out = new Float32Array(n * 64);
  for (let i = 0; i < n; i++) {
    let nrm = 0;
    for (let r = 0; r < 64; r++) {
      let v = 0;
      for (let c = 0; c < 64; c++) v += Wm[r * 64 + c] * descs[i * 64 + c];
      out[i * 64 + r] = v; nrm += v * v;
    }
    nrm = 1 / Math.max(Math.sqrt(nrm), 1e-12);
    for (let r = 0; r < 64; r++) out[i * 64 + r] *= nrm;
  }
  return out;
}

// ---------------------------------------------------------------- matching

// Mutual-nearest-neighbor with cosine threshold. Single pass, no NxM matrix.
export function mnnMatch(d0, n0, d1, n1, minCossim = 0.82) {
  const best01 = new Int32Array(n0).fill(-1);
  const best01v = new Float32Array(n0).fill(-Infinity);
  const best10 = new Int32Array(n1).fill(-1);
  const best10v = new Float32Array(n1).fill(-Infinity);
  for (let i = 0; i < n0; i++) {
    const oi = i * 64;
    for (let j = 0; j < n1; j++) {
      const oj = j * 64;
      let s = 0;
      for (let c = 0; c < 64; c++) s += d0[oi + c] * d1[oj + c];
      if (s > best01v[i]) { best01v[i] = s; best01[i] = j; }
      if (s > best10v[j]) { best10v[j] = s; best10[j] = i; }
    }
  }
  const idx0 = [], idx1 = [];
  for (let i = 0; i < n0; i++) {
    const j = best01[i];
    if (j >= 0 && best10[j] === i && best01v[i] > minCossim) {
      idx0.push(i); idx1.push(j);
    }
  }
  return { idx0, idx1 };
}

/** C4 steering loop, exactly as vismatch xfeat_steerers: steer image-0
 *  descriptors r=0..3, keep the rotation with the most MNN matches.
 *  steererW: null -> perm steerer; Float32Array(64*64) -> learned steerer. */
export function steeredMatch(d0, n0, d1, n1, minCossim, steererW = null, rotations = null) {
  const tTotal = perfNow();
  const timing = { rotations: [] };
  const rots = rotations?.length ? rotations : [0, 1, 2, 3];
  const steered = new Map([[0, d0]]);
  function descForRot(r) {
    if (steered.has(r)) return { desc: steered.get(r), steerMs: 0 };
    const t = perfNow();
    let desc;
    if (steererW) {
      let startRot = 0;
      desc = d0;
      for (const [cachedRot, cachedDesc] of steered) {
        if (cachedRot <= r && cachedRot >= startRot) { startRot = cachedRot; desc = cachedDesc; }
      }
      for (let k = startRot; k < r; k++) desc = applyLearnedSteer(desc, n0, steererW);
    } else {
      desc = applyPermSteer(d0, n0, r);
    }
    const steerMs = perfNow() - t;
    steered.set(r, desc);
    return { desc, steerMs };
  }
  let best = null, rot = rots[0] ?? 0;
  for (const rRaw of rots) {
    const r = ((Math.round(rRaw) % 4) + 4) % 4;
    const { desc, steerMs } = descForRot(r);
    const t = perfNow();
    const cand = mnnMatch(desc, n0, d1, n1, minCossim);
    const mnnMs = perfNow() - t;
    timing.rotations.push({ rot: r, steer_ms: steerMs, mnn_ms: mnnMs, matches: cand.idx0.length });
    postTiming(`relock-timing match rotation rot=${r} steer=${steerMs.toFixed(1)}ms mnn=${mnnMs.toFixed(1)}ms matches=${cand.idx0.length}`);
    if (!best || cand.idx0.length > best.idx0.length) { best = cand; rot = r; }
  }
  timing.best_rot = rot;
  timing.total_ms = perfNow() - tTotal;
  return { ...best, rot, timing };
}

// ---------------------------------------------------------------- homography

function solve8x8(A, b) { // Gaussian elimination with partial pivoting
  const n = 8;
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++)
      if (Math.abs(A[r * n + col]) > Math.abs(A[piv * n + col])) piv = r;
    if (Math.abs(A[piv * n + col]) < 1e-10) return null;
    if (piv !== col) {
      for (let c = 0; c < n; c++) { const t = A[col * n + c]; A[col * n + c] = A[piv * n + c]; A[piv * n + c] = t; }
      const t = b[col]; b[col] = b[piv]; b[piv] = t;
    }
    const d = A[col * n + col];
    for (let r = col + 1; r < n; r++) {
      const f = A[r * n + col] / d;
      if (f === 0) continue;
      for (let c = col; c < n; c++) A[r * n + c] -= f * A[col * n + c];
      b[r] -= f * b[col];
    }
  }
  const x = new Float64Array(n);
  for (let r = n - 1; r >= 0; r--) {
    let s = b[r];
    for (let c = r + 1; c < n; c++) s -= A[r * n + c] * x[c];
    x[r] = s / A[r * n + r];
  }
  return x;
}

// Exact homography from 4 correspondences (h22 = 1)
export function homography4pt(src, dst) {
  const A = new Float64Array(64), b = new Float64Array(8);
  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i], [u, v] = dst[i];
    const r0 = (2 * i) * 8, r1 = (2 * i + 1) * 8;
    A[r0] = x; A[r0 + 1] = y; A[r0 + 2] = 1; A[r0 + 6] = -x * u; A[r0 + 7] = -y * u;
    A[r1 + 3] = x; A[r1 + 4] = y; A[r1 + 5] = 1; A[r1 + 6] = -x * v; A[r1 + 7] = -y * v;
    b[2 * i] = u; b[2 * i + 1] = v;
  }
  const h = solve8x8(A, b);
  return h ? [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1] : null;
}

export function projectPoint(H, x, y) {
  const w = H[6] * x + H[7] * y + H[8];
  return [(H[0] * x + H[1] * y + H[2]) / w, (H[3] * x + H[4] * y + H[5]) / w];
}

// Jacobi eigensolver for symmetric 9x9: eigenvector of the smallest eigenvalue
function minEigvec9(S) {
  const n = 9;
  const A = Float64Array.from(S);
  const V = new Float64Array(n * n);
  for (let i = 0; i < n; i++) V[i * n + i] = 1;
  for (let sweep = 0; sweep < 60; sweep++) {
    let off = 0;
    for (let p = 0; p < n; p++) for (let q = p + 1; q < n; q++) off += A[p * n + q] ** 2;
    if (off < 1e-22) break;
    for (let p = 0; p < n; p++) {
      for (let q = p + 1; q < n; q++) {
        const apq = A[p * n + q];
        if (Math.abs(apq) < 1e-15) continue;
        const theta = (A[q * n + q] - A[p * n + p]) / (2 * apq);
        const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
        const c = 1 / Math.sqrt(t * t + 1), s = t * c;
        for (let k = 0; k < n; k++) {
          const akp = A[k * n + p], akq = A[k * n + q];
          A[k * n + p] = c * akp - s * akq;
          A[k * n + q] = s * akp + c * akq;
        }
        for (let k = 0; k < n; k++) {
          const apk = A[p * n + k], aqk = A[q * n + k];
          A[p * n + k] = c * apk - s * aqk;
          A[q * n + k] = s * apk + c * aqk;
          const vkp = V[k * n + p], vkq = V[k * n + q];
          V[k * n + p] = c * vkp - s * vkq;
          V[k * n + q] = s * vkp + c * vkq;
        }
      }
    }
  }
  let mi = 0;
  for (let i = 1; i < n; i++) if (A[i * n + i] < A[mi * n + mi]) mi = i;
  const v = new Float64Array(n);
  for (let i = 0; i < n; i++) v[i] = V[i * n + mi];
  return v;
}

// Normalized DLT on all points (Hartley normalization + AtA min-eigenvector).
// Optional weights (length N): weighted least squares; rows weighted by weights[k].
export function dltHomography(src, dst, weights = null) {
  const N = src.length;
  let wsum = N;
  if (weights) { wsum = 0; for (let i = 0; i < N; i++) wsum += weights[i]; if (wsum <= 0) return null; }
  const norm = (pts) => {
    let cx = 0, cy = 0;
    for (let i = 0; i < N; i++) { const wi = weights ? weights[i] : 1; cx += pts[i][0] * wi; cy += pts[i][1] * wi; }
    cx /= wsum; cy /= wsum;
    let d = 0;
    for (let i = 0; i < N; i++) { const wi = weights ? weights[i] : 1; d += Math.hypot(pts[i][0] - cx, pts[i][1] - cy) * wi; }
    const s = Math.SQRT2 / Math.max(d / wsum, 1e-12);
    return { s, cx, cy, pts: pts.map(([x, y]) => [(x - cx) * s, (y - cy) * s]) };
  };
  const ns = norm(src), nd = norm(dst);
  const S = new Float64Array(81);
  let rowW = 1;
  const addRow = (row) => {
    for (let i = 0; i < 9; i++) {
      if (row[i] === 0) continue;
      for (let j = i; j < 9; j++) S[i * 9 + j] += rowW * row[i] * row[j];
    }
  };
  for (let k = 0; k < N; k++) {
    rowW = weights ? weights[k] : 1;
    if (rowW <= 0) continue;
    const [x, y] = ns.pts[k], [u, v] = nd.pts[k];
    addRow([x, y, 1, 0, 0, 0, -x * u, -y * u, -u]);
    addRow([0, 0, 0, x, y, 1, -x * v, -y * v, -v]);
  }
  for (let i = 0; i < 9; i++) for (let j = 0; j < i; j++) S[i * 9 + j] = S[j * 9 + i];
  const h = minEigvec9(S);
  const Hn = h, s1 = ns.s, s2 = nd.s;
  const Ts = [s1, 0, -s1 * ns.cx, 0, s1, -s1 * ns.cy, 0, 0, 1];
  const TdInv = [1 / s2, 0, nd.cx, 0, 1 / s2, nd.cy, 0, 0, 1];
  const M = mat3mul(mat3mul(TdInv, Hn), Ts);
  const w = M[8];
  if (!Number.isFinite(w) || Math.abs(w) < 1e-12) return null;
  return M.map((v) => v / w);
}

function mat3mul(a, b) {
  const o = new Array(9).fill(0);
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++)
      for (let k = 0; k < 3; k++) o[i * 3 + j] += a[i * 3 + k] * b[k * 3 + j];
  return o;
}

/** RANSAC homography. src/dst: arrays of [x,y]. Returns {H, inliers, nInliers} */
export function ransacHomography(src, dst, opts = {}) {
  const thresh = opts.thresh ?? 4.0, iters = opts.iters ?? 2000;
  const confidence = opts.confidence ?? 0.995; // early exit when this confident
  const N = src.length;
  if (N < 4) return null;
  let rngState = (opts.seed ?? 1234567) >>> 0;
  const rnd = () => { // xorshift32
    rngState ^= rngState << 13; rngState ^= rngState >>> 17; rngState ^= rngState << 5;
    return (rngState >>> 0) / 4294967296;
  };
  const t2 = thresh * thresh;
  let bestMask = null, bestN = 0;
  let maxIters = iters;
  const s4 = new Array(4), d4 = new Array(4), pick = new Int32Array(4);
  for (let it = 0; it < maxIters; it++) {
    let k = 0;
    while (k < 4) {
      const c = Math.floor(rnd() * N);
      let dup = false;
      for (let m = 0; m < k; m++) if (pick[m] === c) dup = true;
      if (!dup) { pick[k] = c; s4[k] = src[c]; d4[k] = dst[c]; k++; }
    }
    const H = homography4pt(s4, d4);
    if (!H) continue;
    let cnt = 0;
    const mask = new Uint8Array(N);
    for (let i = 0; i < N; i++) {
      const [px, py] = projectPoint(H, src[i][0], src[i][1]);
      const dx = px - dst[i][0], dy = py - dst[i][1];
      if (dx * dx + dy * dy < t2) { mask[i] = 1; cnt++; }
    }
    if (cnt > bestN) {
      bestN = cnt; bestMask = mask;
      // standard adaptive termination: iterations needed to hit `confidence`
      // of drawing one all-inlier 4-sample at the current inlier ratio
      const r4 = Math.min((cnt / N) ** 4, 0.999999);
      if (r4 > 1e-9) {
        maxIters = Math.min(iters, it + 1 + Math.ceil(Math.log(1 - confidence) / Math.log(1 - r4)));
      }
    }
  }
  if (!bestMask || bestN < 4) return null;
  // refit on inliers (twice, like the python reference)
  for (let pass = 0; pass < 2; pass++) {
    const si = [], di = [];
    for (let i = 0; i < N; i++) if (bestMask[i]) { si.push(src[i]); di.push(dst[i]); }
    const H = dltHomography(si, di);
    const mask = new Uint8Array(N);
    let cnt = 0;
    for (let i = 0; i < N; i++) {
      const [px, py] = projectPoint(H, src[i][0], src[i][1]);
      const dx = px - dst[i][0], dy = py - dst[i][1];
      if (dx * dx + dy * dy < t2) { mask[i] = 1; cnt++; }
    }
    if (cnt >= 4) { bestMask = mask; bestN = cnt; }
    if (pass === 1) return { H, inliers: bestMask, nInliers: bestN };
  }
  return null;
}


// ---------------------------------------------------------------- MAGSAC++-style robust homography

function erfc(x) {
  if (x < 0) return 2 - erfc(-x);
  const t = 1 / (1 + 0.3275911 * x);
  return t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 +
         t * (-1.453152027 + t * 1.061405429)))) * Math.exp(-x * x);
}

function degenerateSample(p) {
  for (let skip = 0; skip < 4; skip++) {
    const q = [];
    for (let i = 0; i < 4; i++) if (i !== skip) q.push(p[i]);
    const ax = q[1][0] - q[0][0], ay = q[1][1] - q[0][1];
    const bx = q[2][0] - q[0][0], by = q[2][1] - q[0][1];
    const cross = Math.abs(ax * by - ay * bx);
    if (cross < 1e-3 * Math.hypot(ax, ay) * Math.hypot(bx, by)) return true;
  }
  return false;
}

export function magsacHomography(src, dst, opts = {}) {
  const sigmaMax = opts.sigmaMax ?? 3.0;
  const iters = opts.iters ?? 2000;
  const confidence = opts.confidence ?? 0.95;
  const polishIters = opts.polishIters ?? 4;
  const reportThresh = opts.reportThresh ?? sigmaMax;
  const N = src.length;
  if (N < 4) return null;
  let rngState = (opts.seed ?? 1234567) >>> 0;
  const rnd = () => { rngState ^= rngState << 13; rngState ^= rngState >>> 17; rngState ^= rngState << 5; return (rngState >>> 0) / 4294967296; };
  let order = null;
  if (opts.scores && opts.scores.length === N) {
    order = Array.from({ length: N }, (_, i) => i).sort((a, b) => opts.scores[b] - opts.scores[a]);
  }
  const poolSize = (it) => order ? Math.min(N, Math.max(7, 4 + Math.floor((N - 4) * it / (0.5 * iters + 1)))) : N;
  const invS = 1 / (Math.SQRT2 * sigmaMax);
  const tau = 3.04 * sigmaMax, tau2 = tau * tau, rt2 = reportThresh * reportThresh;
  const residual2 = (H, i) => {
    const w = H[6] * src[i][0] + H[7] * src[i][1] + H[8];
    if (!Number.isFinite(w) || Math.abs(w) < 1e-12) return Infinity;
    const px = (H[0] * src[i][0] + H[1] * src[i][1] + H[2]) / w;
    const py = (H[3] * src[i][0] + H[4] * src[i][1] + H[5]) / w;
    if (!Number.isFinite(px) || !Number.isFinite(py)) return Infinity;
    const dx = px - dst[i][0], dy = py - dst[i][1];
    return dx * dx + dy * dy;
  };
  const scoreModel = (H) => {
    let sc = 0, cnt = 0;
    for (let i = 0; i < N; i++) {
      const r2 = residual2(H, i);
      if (r2 < tau2) sc += erfc(Math.sqrt(r2) * invS);
      if (r2 < rt2) cnt++;
    }
    return { sc, cnt };
  };
  const wbuf = new Float64Array(N);
  const polish = (H0) => {
    let H = H0;
    for (let it = 0; it < polishIters; it++) {
      let nz = 0;
      for (let i = 0; i < N; i++) {
        const r2 = residual2(H, i);
        wbuf[i] = r2 < tau2 ? erfc(Math.sqrt(r2) * invS) : 0;
        if (wbuf[i] > 0) nz++;
      }
      if (nz < 4) return H;
      const Hn = dltHomography(src, dst, wbuf);
      if (!Hn) return H;
      H = Hn;
    }
    return H;
  };
  let bestH = null, bestScore = -Infinity, bestCnt = 0, maxIters = iters;
  const s4 = new Array(4), d4 = new Array(4), pick = new Int32Array(4);
  for (let it = 0; it < maxIters; it++) {
    const pool = poolSize(it);
    let k = 0, guard = 0;
    while (k < 4 && guard++ < 64) {
      const c = Math.floor(rnd() * pool);
      const idx = order ? order[c] : c;
      let dup = false;
      for (let m = 0; m < k; m++) if (pick[m] === idx) dup = true;
      if (!dup) { pick[k] = idx; s4[k] = src[idx]; d4[k] = dst[idx]; k++; }
    }
    if (k < 4 || degenerateSample(s4) || degenerateSample(d4)) continue;
    const Hm = homography4pt(s4, d4);
    if (!Hm) continue;
    let { sc, cnt } = scoreModel(Hm);
    if (sc <= bestScore) continue;
    const Hp = polish(Hm);
    const sp = scoreModel(Hp);
    let H = Hm;
    if (sp.sc > sc) { H = Hp; sc = sp.sc; cnt = sp.cnt; }
    if (sc > bestScore) {
      bestScore = sc; bestH = H; bestCnt = cnt;
      const r4 = Math.min((cnt / N) ** 4, 0.999999);
      if (r4 > 1e-9) maxIters = Math.min(iters, it + 1 + Math.ceil(Math.log(1 - confidence) / Math.log(1 - r4)));
    }
  }
  if (!bestH || bestCnt < 4) return null;
  const Hf = polish(bestH);
  const sf = scoreModel(Hf);
  const H = sf.sc >= bestScore ? Hf : bestH;
  const mask = new Uint8Array(N);
  let nInliers = 0;
  for (let i = 0; i < N; i++) if (residual2(H, i) < rt2) { mask[i] = 1; nInliers++; }
  return { H, inliers: mask, nInliers, score: sf.sc >= bestScore ? sf.sc : bestScore };
}

export function convexHullSpread(src, mask, imageSize = 1200) {
  const pts = [];
  for (let i = 0; i < src.length; i++) if (mask[i]) pts.push(src[i]);
  if (pts.length < 3) return 0;
  pts.sort((a, b) => a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]);
  const cross = (o, a, b) => (a[0]-o[0])*(b[1]-o[1]) - (a[1]-o[1])*(b[0]-o[0]);
  const lower = [];
  for (const p of pts) { while (lower.length >= 2 && cross(lower[lower.length-2], lower[lower.length-1], p) <= 0) lower.pop(); lower.push(p); }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) { const p = pts[i]; while (upper.length >= 2 && cross(upper[upper.length-2], upper[upper.length-1], p) <= 0) upper.pop(); upper.push(p); }
  const hull = lower.slice(0, -1).concat(upper.slice(0, -1));
  let area = 0;
  for (let i = 0; i < hull.length; i++) { const a = hull[i], b = hull[(i+1)%hull.length]; area += a[0]*b[1] - a[1]*b[0]; }
  return Math.abs(area) * 0.5 / (imageSize * imageSize);
}

export function bestHomography(src, dst, opts = {}) {
  const threshs = opts.threshs ?? [3];
  const method = opts.method ?? "magsac";
  const imageSize = opts.imageSize ?? 1200;
  let best = null;
  for (const t of threshs) {
    const res = method === "magsac"
      ? magsacHomography(src, dst, { ...opts, sigmaMax: t, reportThresh: opts.reportThresh ?? t })
      : ransacHomography(src, dst, { ...opts, thresh: t });
    if (!res) continue;
    const spread = convexHullSpread(src, res.inliers, imageSize);
    const candScore = res.nInliers >= 5 ? res.nInliers * Math.sqrt(spread) : 0;
    if (!best || candScore > best.candScore) best = { ...res, spread, candScore, thresh: t };
  }
  return best;
}

/** Convert a 3x3 homography (image-A px -> image-B px) into a CSS matrix3d
 *  string. scaleA/scaleB: CSS px per image px of each element. Apply to the
 *  image-A element with transform-origin: 0 0. */
export function homographyToMatrix3d(H, scaleA = 1, scaleB = 1) {
  // T = S_B * H * S_A^-1  (column-major for CSS)
  const m = [
    H[0] * scaleB / scaleA, H[3] * scaleB / scaleA, 0, H[6] / scaleA,
    H[1] * scaleB / scaleA, H[4] * scaleB / scaleA, 0, H[7] / scaleA,
    0, 0, 1, 0,
    H[2] * scaleB, H[5] * scaleB, 0, H[8],
  ];
  return `matrix3d(${m.map((v) => v.toPrecision(8)).join(",")})`;
}
